import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input,
    Button, Spinner, Alert, FormFeedback, Badge, Collapse, Modal, ModalHeader,
    ModalBody, ModalFooter
} from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import PhoneInput from "../../components/Forms/PhoneInput";
import { CURRENCIES, defaultInstallmentEnabled } from "../../constants/paymentConstants";
import "../../assets/css/page-common.css";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import { fetchInstallmentGroups, createInstantLink } from "../../api/requests/payment-requests";
import { fetchPayTRRates, saveGroup } from "../../api/requests/company-requests";
import { fetchAccountsMe } from "../../api/requests/account-requests";
import BackButton from "../../components/Common/BackButton";
import DateTimeInput, { nowLocalDateTime } from "../../components/Common/DateTimeInput";
import { getPaymentWords } from "../../utils/paymentWord";

const CARD_TYPES = [
    "WORLD", "AXESS", "MAXIMUM", "CARDFINANS", "PARAF", "ADVANTAGE", "COMBO", "BONUS", "SAGLAMKART", "STANDART"
];

const ACCOUNT_TYPES = [
    { value: "SOLE_PROPRIETORSHIP", label: "Şahıs Şirketi" },
    { value: "JOINT_STOCK_COMPANY", label: "Anonim Şirket" },
    { value: "LIMITED_COMPANY", label: "Limited Şirket" },
    { value: "COOPERATIVE", label: "Kooperatif" },
    { value: "GENERAL_PARTNERSHIP", label: "Kolektif Şirket" },
    { value: "COMMANDITE_COMPANY", label: "Komandit Şirket" }
];

const MAX_INSTALLMENTS = 12;

export default function InstantPaymentCreatePage({ companyId: companyIdProp, onSaved, onSuccess }) {
    const navigate = useNavigate();
    const { companyId: companyIdParam } = useParams();
    const [searchParams] = useSearchParams();
    const companyId = companyIdProp || companyIdParam || null;

    // Vade Takibi'nden gelen prefill değerleri
    const prefilledAccountId    = searchParams.get("accountId")     || "";
    const prefilledAmount       = searchParams.get("amount")        || "";
    const prefilledDebtMovementId = searchParams.get("debtMovementId") || "";
    // Borcun para birimi. Linkin para birimi backend'de bağlı borç hareketinden alınır;
    // burada yalnızca doğru sembolü ve açıklamayı göstermek için kullanılır.
    const prefilledCurrency = (searchParams.get("currency") || "TRY").toUpperCase();

    // --- State ---
    const [roles, setRoles] = useState([]);
    const [loadingUser, setLoadingUser] = useState(true);

    const [cariGroups, setCariGroups] = useState([]);
    const [filteredGroups, setFilteredGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(true);

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);

    const [error, setError] = useState("");
    const [link, setLink] = useState("");
    const [showRates, setShowRates] = useState(false);

    const [isMobile, setIsMobile] = useState(false);
    const [displayFixedAmount, setDisplayFixedAmount] = useState(() => {
        if (!prefilledAmount) return "";
        const num = Number(prefilledAmount);
        return isNaN(num) ? "" : num.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });

    const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
    const [loadingGroupSave, setLoadingGroupSave] = useState(false);
    const [groupData, setGroupData] = useState({
        groupName: "",
        description: "",
        rates: {}
    });

    const timerRef = useRef(null);

    const pw = getPaymentWords(roles);

    useEffect(() => {
        document.title = `${pw.Word} Linki Oluştur | Tahsilat360`;
    }, [pw.Word]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 576);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const loadAuthMe = async () => {
            setLoadingUser(true);
            try {
                const res = await fetchAuthMe();
                const json = await res.json();
                if (res.ok && json.data?.roles) setRoles(json.data.roles);
            } catch { /* ignore */ }
            finally { setLoadingUser(false); }
        };
        loadAuthMe();
    }, []);

    const isAdmin = useMemo(
        () => roles.includes("ROLE_SUPER_ADMIN") || roles.includes("ROLE_COMPANY_ADMIN"),
        [roles]
    );

    useEffect(() => {
        if (!loadingUser && isAdmin) fetchUserAccounts();
    }, [loadingUser, isAdmin]);

    // Vade Takibi'nden gelindiğinde cariyi otomatik seç
    useEffect(() => {
        if (!prefilledAccountId || accounts.length === 0) return;
        const acc = accounts.find((a) => a.id === prefilledAccountId);
        if (acc) {
            setSelectedAccount(acc);
            formik.setFieldValue("billingOption", "existing_account");
            formik.setFieldValue("accountId", prefilledAccountId);
        }
    }, [accounts, prefilledAccountId]);

    const fetchUserAccounts = async () => {
        try {
            const res = await fetchAccountsMe();
            const json = await res.json();
            if (res.ok) setAccounts(json.data || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        if (!companyId) return;
        fetchMyAccountGroups();
    }, [companyId]);

    const fetchMyAccountGroups = async () => {
        setLoadingGroups(true);
        setError("");
        setLink("");
        try {
            const response = await fetchInstallmentGroups();
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || "Cari/Bayi grup sorgusu başarısız.");
            const groups = result.data || [];
            setCariGroups(groups);
            setFilteredGroups(groups);
        } catch (err) {
            setError("Taksit grubu verisi alınamadı");
        } finally {
            setLoadingGroups(false);
        }
    };

    const defaultExpiryDate = useMemo(() => {
        return new Date(Date.now() + 27 * 60 * 60 * 1000)
            .toISOString()
            .slice(0, 16);
    }, []);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            accountGroupId: "",
            linkType: "ONE_TIME",
            expiryDate: defaultExpiryDate,
            minAmount: "",
            maxAmount: "",
            maxUsageCount: "",
            fixedAmount: prefilledAmount ? Number(prefilledAmount) : "",
            // Vade Takibi'nden gelindiyse borcun birimi; elle oluşturmada kullanıcı seçer.
            currency: prefilledCurrency,
            billingOption: prefilledAccountId ? "existing_account" : "",
            accountId: prefilledAccountId || "",
            invoiceAccountType: "",
            invoiceCompanyName: "",
            invoiceTaxNumber: "",
            invoiceTcNumber: "",
            invoiceTaxOffice: "",
            invoiceAddress: "",
            invoiceEmail: "",
            invoicePhone: ""
        },
        validationSchema: Yup.object({
            accountGroupId: Yup.string().required("Grup seçiniz"),
            linkType: Yup.string().oneOf(["ONE_TIME", "FIXED"]).required(),
            // Sabit linkte bitiş tarihi opsiyoneldir
            expiryDate: Yup.string().when("linkType", {
                is: "FIXED",
                then: (s) => s.notRequired(),
                otherwise: (s) => s.required("Bitiş tarihi giriniz")
            }),
            minAmount: Yup.number()
                .typeError("Geçerli bir tutar giriniz")
                .min(10, "Minimum tutar en az 10 TL olmalıdır")
                .nullable()
                .transform((v, o) => (String(o).trim() === "" ? null : v)),
            maxAmount: Yup.number()
                .typeError("Geçerli bir tutar giriniz")
                .nullable()
                .transform((v, o) => (String(o).trim() === "" ? null : v))
                .test("gt-min", "Maksimum tutar minimum tutardan büyük olmalıdır", function (v) {
                    const min = this.parent.minAmount;
                    if (v == null || min == null || String(min).trim() === "") return true;
                    return Number(v) > Number(min);
                }),
            maxUsageCount: Yup.number()
                .typeError("Geçerli bir sayı giriniz")
                .integer("Tam sayı olmalıdır")
                .min(1, "En az 1 olmalıdır")
                .nullable()
                .transform((v, o) => (String(o).trim() === "" ? null : v)),
            ...(isAdmin
                ? {
                    billingOption: Yup.string().required("Lütfen bir seçenek belirleyin"),
                    accountId: Yup.string().when("billingOption", {
                        is: "existing_account",
                        then: (s) => s.required("Cari/Bayi seçiniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceAccountType: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Cari/Bayi tipi seçiniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceCompanyName: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Şirket adı gereklidir"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceTaxOffice: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Vergi dairesi gereklidir"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceAddress: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Adres gereklidir"),
                        otherwise: (s) => s.notRequired()
                    }),
                    fixedAmount: Yup.number()
                        .typeError("Geçerli bir tutar giriniz")
                        .positive("Tutar 0'dan büyük olmalıdır")
                        .nullable()
                        .transform((v, o) => (String(o).trim() === "" ? null : v)),
                    invoiceEmail: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.email("Geçerli bir email adresi giriniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoicePhone: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) =>
                            s.matches(/^[0-9+\-\s()]+$/, "Geçerli bir telefon numarası giriniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceTcNumber: Yup.string().when(["billingOption", "invoiceAccountType"], {
                        is: (bo, at) => bo === "new_invoice" && at === "SOLE_PROPRIETORSHIP",
                        then: (s) =>
                            s.required("TC kimlik numarası gereklidir").matches(/^\d{11}$/, "TC kimlik numarası 11 haneli olmalıdır"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceTaxNumber: Yup.string().when(["billingOption", "invoiceAccountType"], {
                        is: (bo, at) => bo === "new_invoice" && at && at !== "SOLE_PROPRIETORSHIP",
                        then: (s) =>
                            s.required("Vergi numarası gereklidir").matches(/^\d{10}$/, "Vergi numarası 10 haneli olmalıdır"),
                        otherwise: (s) => s.notRequired()
                    })
                }
                : {})
        }),
        onSubmit: async (values, { setSubmitting }) => {
            setError("");
            setLink("");
            try {
                const group = cariGroups.find((g) => g.id === values.accountGroupId);
                const cardsMaxInstallments = {};
                (group?.installmentRates || []).forEach((r) => {
                    if (r.enabled) {
                        cardsMaxInstallments[r.cardBrand] = Math.max(
                            cardsMaxInstallments[r.cardBrand] || 0,
                            r.installmentCount
                        );
                    }
                });

                const isFixedLink = values.linkType === "FIXED";
                const hasFixedAmount = String(values.fixedAmount ?? "").trim() !== "";
                const dto = {
                    companyId,
                    accountGroupId: values.accountGroupId,
                    cardsMaxInstallments,
                    linkType: values.linkType,
                    ...(values.expiryDate ? { expiryDate: values.expiryDate } : {}),
                    ...(hasFixedAmount ? { fixedAmount: Number(values.fixedAmount) } : {}),
                    // Sabit tutar döviz olabilir; bağlı borç varsa backend borcun birimini kullanır.
                    ...(hasFixedAmount ? { currency: values.currency } : {}),
                    // Sabit link + serbest tutar: ödeyenin girebileceği sınırlar
                    ...(isFixedLink && !hasFixedAmount && String(values.minAmount ?? "").trim() !== ""
                        ? { minAmount: Number(values.minAmount) } : {}),
                    ...(isFixedLink && !hasFixedAmount && String(values.maxAmount ?? "").trim() !== ""
                        ? { maxAmount: Number(values.maxAmount) } : {}),
                    ...(isFixedLink && String(values.maxUsageCount ?? "").trim() !== ""
                        ? { maxUsageCount: Number(values.maxUsageCount) } : {}),
                    ...(isAdmin && values.billingOption === "existing_account" && { accountId: values.accountId }),
                    // Vade Takibi'nden gelen borç hareketi — backend bu link ödendiğinde
                    // ilgili borç AccountMovement'ini CLOSED yapacak.
                    ...(prefilledDebtMovementId
                        && isAdmin
                        && values.billingOption === "existing_account"
                        && values.accountId === prefilledAccountId
                        && { linkedDebtMovementId: prefilledDebtMovementId }),
                    ...(isAdmin && values.billingOption === "new_invoice" && {
                        invoiceAccountType: values.invoiceAccountType,
                        invoiceCompanyName: values.invoiceCompanyName,
                        invoiceTaxNumber: values.invoiceTaxNumber,
                        invoiceTcNumber: values.invoiceTcNumber,
                        invoiceAddress: values.invoiceAddress,
                        invoiceTaxOffice: values.invoiceTaxOffice,
                        ...(values.invoiceEmail && { invoiceEmail: values.invoiceEmail }),
                        ...(values.invoicePhone && { invoicePhone: values.invoicePhone })
                    })
                };

                const resp = await createInstantLink(dto);
                const json = await resp.json();

                if (resp.ok) {
                    setLink(json.data);
                    toast.success(`Oluşturulan bağlantı, “${pw.Word} Linkleri” sayfasına eklendi`);
                    onSuccess && onSuccess();
                    onSaved && onSaved();
                    if (timerRef.current) clearTimeout(timerRef.current);
                    timerRef.current = setTimeout(() => {
                        navigate("/payment-links");
                    }, 2000);
                } else {
                    setError(json.message || "Link oluşturulamadı");
                    toast.error(json.message || "Link oluşturulamadı");
                }
            } catch (err) {
                console.error(err);
                setError("Link oluşturulurken hata oluştu");
                toast.error("Link oluşturulurken hata oluştu");
            } finally {
                setSubmitting(false);
            }
        }
    });

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    useEffect(() => {
        if (filteredGroups.length > 0 && !formik.values.accountGroupId) {
            formik.setFieldValue("accountGroupId", filteredGroups[0].id);
        }
    }, [filteredGroups]);

    useEffect(() => {
        if (!selectedAccount || formik.values.billingOption !== "existing_account") {
            setFilteredGroups(cariGroups);
            if (formik.values.billingOption !== "existing_account") {
                formik.setFieldValue("accountGroupId", "");
            }
            return;
        }
        if (selectedAccount.accountGroupId) {
            const onlyGroup = cariGroups.filter((g) => g.id === selectedAccount.accountGroupId);
            setFilteredGroups(onlyGroup);
            formik.setFieldValue("accountGroupId", onlyGroup.length === 1 ? onlyGroup[0].id : "");
        } else {
            setFilteredGroups([]);
            formik.setFieldValue("accountGroupId", "");
        }
    }, [selectedAccount, cariGroups, formik.values.billingOption]);

    const showToast = (type, msg) => {
        const map = { danger: "error", error: "error" };
        const fn = toast[map[type] || type] || toast.info;
        fn(msg, { autoClose: 3000 });
    };

    const getCardBrandColor = (brand) => {
        const colors = {
            VISA: "primary", MASTERCARD: "warning", AMERICAN_EXPRESS: "info",
            WORLD: "success", TROY: "secondary", MAXIMUM: "danger"
        };
        return colors[brand] || "dark";
    };

    const getCardBrandName = (brand) => {
        const names = {
            VISA: "Visa", MASTERCARD: "Mastercard", AMERICAN_EXPRESS: "American Express",
            WORLD: "World", TROY: "Troy", MAXIMUM: "Maximum"
        };
        return names[brand] || brand;
    };

    const handleBillingOptionChange = (e) => {
        const newOption = e.target.value;
        formik.setFieldValue("billingOption", newOption);

        if (newOption === "existing_account") {
            formik.setFieldValue("invoiceAccountType", "");
            formik.setFieldValue("invoiceCompanyName", "");
            formik.setFieldValue("invoiceTaxNumber", "");
            formik.setFieldValue("invoiceTcNumber", "");
            formik.setFieldValue("invoiceTaxOffice", "");
            formik.setFieldValue("invoiceAddress", "");
            formik.setFieldValue("invoiceEmail", "");
            formik.setFieldValue("invoicePhone", "");
            formik.setTouched({
                ...formik.touched, invoiceAccountType: false, invoiceCompanyName: false, invoiceTaxNumber: false,
                invoiceTcNumber: false, invoiceTaxOffice: false, invoiceAddress: false, invoiceEmail: false, invoicePhone: false, accountId: false
            });
        } else if (newOption === "new_invoice") {
            formik.setFieldValue("accountId", "");
            setSelectedAccount(null);
            formik.setTouched({ ...formik.touched, accountId: false });
        } else {
            formik.setFieldValue("accountId", "");
            formik.setFieldValue("invoiceAccountType", "");
            formik.setFieldValue("invoiceCompanyName", "");
            formik.setFieldValue("invoiceTaxNumber", "");
            formik.setFieldValue("invoiceTcNumber", "");
            formik.setFieldValue("invoiceTaxOffice", "");
            formik.setFieldValue("invoiceAddress", "");
            formik.setFieldValue("invoiceEmail", "");
            formik.setFieldValue("invoicePhone", "");
            setSelectedAccount(null);
            formik.setTouched({
                ...formik.touched,
                accountId: false, invoiceAccountType: false, invoiceCompanyName: false, invoiceTaxNumber: false,
                invoiceTcNumber: false, invoiceTaxOffice: false, invoiceAddress: false, invoiceEmail: false, invoicePhone: false
            });
        }
    };

    const handleAccountChange = (e) => {
        const accountId = e.target.value;
        formik.setFieldValue("accountId", accountId);
        const acc = accounts.find((a) => a.id === accountId);
        setSelectedAccount(acc || null);
    };

    const handleResetAccount = () => {
        formik.setFieldValue("accountId", "");
        setSelectedAccount(null);
    };

    const handleAccountTypeChange = (e) => {
        const accountType = e.target.value;
        formik.setFieldValue("invoiceAccountType", accountType);
        formik.setFieldValue("invoiceTaxNumber", "");
        formik.setFieldValue("invoiceTcNumber", "");
        formik.setTouched({ ...formik.touched, invoiceTaxNumber: false, invoiceTcNumber: false });
    };

    const handleFixedAmountChange = (e) => {
        const raw = (e.target.value || "").trim();

        const hasComma = raw.includes(",");
        const dotMatches = raw.match(/\./g) || [];
        const hasDot = dotMatches.length > 0;

        let intPartRaw = raw;
        let fracPartRaw = "";
        let hadExplicitDecimal = false;

        if (hasComma) {
            hadExplicitDecimal = true;
            const m = raw.match(/^(.*?),(.*)$/);
            intPartRaw = m ? m[1] : raw;
            fracPartRaw = m ? m[2] : "";
        } else if (hasDot) {
            if (dotMatches.length === 1 && /\.\d{0,2}$/.test(raw)) {
                hadExplicitDecimal = true;
                const m = raw.match(/^(.*?)\.(.*)$/);
                intPartRaw = m ? m[1] : raw;
                fracPartRaw = m ? m[2] : "";
            } else {
                hadExplicitDecimal = false;
                intPartRaw = raw;
                fracPartRaw = "";
            }
        }

        const intDigits = (intPartRaw || "").replace(/\D/g, "");
        const fracDigits = (fracPartRaw || "").replace(/\D/g, "").slice(0, 2);

        let normalized = "";
        if (intDigits.length) {
            const intNoLeadingZeros = String(parseInt(intDigits, 10));
            normalized = intNoLeadingZeros === "NaN" ? "" : intNoLeadingZeros;
        }
        if (hadExplicitDecimal) {
            if (!normalized) normalized = "0";
            normalized += "." + fracDigits;
        }

        const groupThousands = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const groupedInt = intDigits ? groupThousands(intDigits) : hadExplicitDecimal ? "0" : "";

        let display = groupedInt;
        if (hadExplicitDecimal) {
            if (raw.endsWith(",") || raw.endsWith(".")) display = `${groupedInt},`;
            else display = `${groupedInt},${fracDigits}`;
        }

        formik.setFieldValue("fixedAmount", normalized);
        setDisplayFixedAmount(display);
    };

    // installment helpers (render)
    const groupInstallmentsByCard = (installmentRates) => {
        if (!installmentRates) return {};
        return installmentRates.reduce((acc, rate) => {
            if (!acc[rate.cardBrand]) acc[rate.cardBrand] = [];
            if (rate.enabled) acc[rate.cardBrand].push(rate);
            return acc;
        }, {});
    };

    const renderInstallmentCards = () => {
        const group = cariGroups.find((g) => g.id === formik.values.accountGroupId);
        if (!group || !group.installmentRates) {
            return <Alert color="warning" className="border-0 rounded-4">Taksit bilgisi bulunamadı.</Alert>;
        }
        const grouped = groupInstallmentsByCard(group.installmentRates);
        const brands = Object.keys(grouped);
        if (brands.length === 0) {
            return <Alert color="warning" className="border-0 rounded-4">Aktif taksit oranı yok.</Alert>;
        }
        return (
            <Row>
                {brands.map((brand) => (
                    <Col xs={12} sm={6} lg={6} xl={4} key={brand} className="mb-3">
                        <Card className="border-0 shadow-sm rounded-4">
                            <CardHeader className="bg-light py-2 rounded-top-4">
                                <Badge color={getCardBrandColor(brand)} className="me-2">{getCardBrandName(brand)}</Badge>
                                <small>{grouped[brand].length} seçenek</small>
                            </CardHeader>
                            <CardBody className="p-2">
                                <table className="table table-sm mb-0">
                                    <thead>
                                    <tr>
                                        <th>Taksit</th>
                                        <th>Komisyon %</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {grouped[brand]
                                        .sort((a, b) => a.installmentCount - b.installmentCount)
                                        .map((r) => (
                                            <tr key={r.id}>
                                                <td>{r.installmentCount}</td>
                                                <td>{r.commissionPercent.toFixed(2)}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    };

    // Yeni Grup modal yardımcıları
    const convertBackendToFrontend = (installmentRates = []) => {
        const rates = {};
        installmentRates.forEach((r) => {
            const brand = (r.brand ?? r.cardBrand ?? "").toUpperCase();
            if (!rates[brand]) rates[brand] = {};
            rates[brand][r.installmentCount] = {
                id: r.id ?? null,
                value: Number(r.commissionPercent ?? r.commissionRate ?? 0).toFixed(2),
                enabled: r.enabled != null ? r.enabled : defaultInstallmentEnabled(brand)
            };
        });
        CARD_TYPES.forEach((card) => {
            if (!rates[card]) rates[card] = {};
            for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
                if (!rates[card][i]) rates[card][i] = { id: null, value: "0.00", enabled: defaultInstallmentEnabled(card) };
            }
        });
        return rates;
    };

    const convertFrontendToBackend = (ratesObj = {}) => {
        const list = [];
        Object.entries(ratesObj).forEach(([cardBrand, counts]) => {
            Object.entries(counts).forEach(([instCnt, { id, value, enabled }]) => {
                if (value != null && value !== "") {
                    const commission = parseFloat(value);
                    if (!Number.isNaN(commission)) {
                        list.push({
                            id, cardBrand,
                            installmentCount: parseInt(instCnt, 10),
                            commissionPercent: commission,
                            enabled
                        });
                    }
                }
            });
        });
        return list;
    };

    const handleFetchPayTRRates = async () => {
        if (!companyId) return;
        try {
            setLoadingGroupSave(true);
            const json = await fetchPayTRRates(companyId);
            if (Array.isArray(json.data)) {
                const payTRRates = convertBackendToFrontend(json.data);
                setGroupData((prev) => ({ ...prev, rates: payTRRates }));
                showToast("success", "PayTR taksit oranları yüklendi");
            } else {
                showToast("danger", json.message || "Oranlar yüklenemedi");
            }
        } catch (err) {
            console.error("PayTR rates error:", err);
            showToast("danger", "PayTR oranları alınamadı");
        } finally {
            setLoadingGroupSave(false);
        }
    };

    const openAddGroupModal = () => {
        setGroupData({ groupName: "", description: "", rates: convertBackendToFrontend([]) });
        setAddGroupModalOpen(true);
    };

    const handleGroupChange = (e) => {
        const { name, value } = e.target;
        setGroupData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRateChange = (card, inst, newVal) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...(prev.rates?.[card] || {}),
                    [inst]: {
                        id: prev.rates?.[card]?.[inst]?.id || null,
                        value: newVal,
                        enabled: prev.rates?.[card]?.[inst]?.enabled !== false
                    }
                }
            }
        }));
    };

    const handleEnabledChange = (card, inst, checked) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...prev.rates[card],
                    [inst]: {
                        id: prev.rates[card]?.[inst]?.id || null,
                        value: prev.rates[card]?.[inst]?.value || "0.00",
                        enabled: checked
                    }
                }
            }
        }));
    };

    const handleSaveGroup = async () => {
        const groupName = groupData?.groupName?.trim();
        if (!groupName) { showToast("warning", "Grup adı gereklidir"); return; }

        // Vade Takibi'nden gelen ve grubu olmayan cari için aynı çağrıda atama yap.
        const shouldAssignToAccount = !!(selectedAccount && !selectedAccount.accountGroupId);

        const payload = {
            groupName,
            description: groupData?.description?.trim() || "",
            companyId,
            installmentRates: convertFrontendToBackend(groupData?.rates || {}),
            ...(shouldAssignToAccount && { assignToAccountId: selectedAccount.id })
        };

        try {
            setLoadingGroupSave(true);
            const result = await saveGroup(payload, false);
            showToast("success", "Taksit grubu oluşturuldu");
            setAddGroupModalOpen(false);
            await fetchMyAccountGroups();

            // Cari'ye atadıysak — yerel state'leri yeni accountGroupId ile güncelle.
            // useEffect (selectedAccount, cariGroups) bağımlılığı ile dropdown otomatik dolar.
            const newGroupId = result?.data?.id;
            if (shouldAssignToAccount && newGroupId) {
                setSelectedAccount((prev) =>
                    prev ? { ...prev, accountGroupId: newGroupId } : prev
                );
                setAccounts((prev) =>
                    prev.map((a) =>
                        a.id === selectedAccount.id ? { ...a, accountGroupId: newGroupId } : a
                    )
                );
            }
        } catch (error) {
            console.error("Group save error:", error);
            showToast("danger", "Kaydetme hatası");
        } finally {
            setLoadingGroupSave(false);
        }
    };

    const flowChosen = !isAdmin || !!formik.values.billingOption;
    const usingExisting = isAdmin && formik.values.billingOption === "existing_account";
    const usingNewInvoice = isAdmin && formik.values.billingOption === "new_invoice";

    const handleSubmitVisibleOnly = async (e) => {
        e.preventDefault();
        const visible = ["accountGroupId", "expiryDate"];

        if (isAdmin && formik.values.billingOption === "existing_account") {
            visible.push("billingOption", "accountId");
        } else if (isAdmin && formik.values.billingOption === "new_invoice") {
            visible.push(
                "billingOption", "invoiceAccountType", "invoiceCompanyName", "invoiceTaxOffice",
                "invoiceAddress", "invoiceEmail", "invoicePhone"
            );
            if (formik.values.invoiceAccountType === "SOLE_PROPRIETORSHIP") {
                visible.push("invoiceTcNumber");
            } else if (formik.values.invoiceAccountType) {
                visible.push("invoiceTaxNumber");
            }
        } else if (isAdmin) {
            visible.push("billingOption");
        }

        const errors = await formik.validateForm();
        const touched = {};
        visible.forEach((f) => (touched[f] = true));
        formik.setTouched(touched);

        const visibleErrors = {};
        visible.forEach((f) => { if (errors[f]) visibleErrors[f] = errors[f]; });

        if (Object.keys(visibleErrors).length > 0) {
            const firstError = visibleErrors[Object.keys(visibleErrors)[0]];
            showToast("warning", firstError);
        } else {
            formik.handleSubmit(e);
        }
    };

    // UI
    const NAVY = "#221C5D";

    if (loadingGroups && !cariGroups.length) {
        return (
            <div className="page-content text-center p-5">
                <Spinner size="lg" color="primary" />
                <h6 className="mt-3 text-muted">Yükleniyor...</h6>
            </div>
        );
    }

    return (
        <div className="page-content page-common">
            <div className="container-fluid">

                {/* ── Page Header ── */}
                <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <BackButton />
                        <div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                {pw.Word} Linki Oluştur
                            </h5>
                            <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                Anlık {pw.Word} Linki Oluşturma Sayfası
                            </span>
                        </div>
                    </div>
                </div>

                <Row>
                    <Col lg={12}>
                        {/* 1) Cari/fatura seçimi */}
                        <Card className="detail-card">
                            <CardHeader>
                                <div className="section-title">
                                    <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                        <i className="bx bx-link-alt" />
                                    </div>
                                    {pw.Word} Linki Bilgileri
                                </div>
                            </CardHeader>

                            <CardBody className="p-4">
                                {error && (
                                    <Alert color="danger" className="border-0 rounded-4">
                                        <i className="bx bx-error me-2" />
                                        {error}
                                    </Alert>
                                )}

                                {/* Admin akış seçimi */}
                                {isAdmin && (
                                    <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                        <h6 className="mb-3 fw-semibold">Cari/Fatura Seçenekleri</h6>
                                        <FormGroup className="mb-0">
                                            <Input
                                                type="select"
                                                name="billingOption"
                                                onChange={handleBillingOptionChange}
                                                value={formik.values.billingOption}
                                                invalid={formik.touched.billingOption && !!formik.errors.billingOption}
                                                className="rounded-3"
                                            >
                                                <option value="">-- Seçiniz --</option>
                                                <option value="existing_account">Mevcut cari/bayi kullan</option>
                                                <option value="new_invoice">Yeni fatura bilgileri gir</option>
                                                <option value="no_invoice">Fatura veya cari/bayi seçmeden devam et</option>
                                            </Input>
                                            {formik.touched.billingOption && (
                                                <FormFeedback>{formik.errors.billingOption}</FormFeedback>
                                            )}
                                        </FormGroup>
                                    </div>
                                )}

                                {/* Akış rehberi */}
                                {!flowChosen ? (
                                    <Alert color="info" className="border-0 rounded-4">
                                        Devam etmek için önce <strong>“Cari/Fatura Seçenekleri”</strong> alanından bir seçim yapın.
                                    </Alert>
                                ) : (
                                    <>
                                        {/* existing_account ise cari seç */}
                                        {usingExisting && (
                                            <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                                <h6 className="mb-3 fw-semibold">Cari/Bayi Seç</h6>
                                                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
                                                    <Input
                                                        type="select"
                                                        name="accountId"
                                                        onChange={handleAccountChange}
                                                        value={formik.values.accountId}
                                                        invalid={formik.touched.accountId && !!formik.errors.accountId}
                                                        className="me-2 rounded-3"
                                                    >
                                                        <option value="">-- Cari/Bayi seçiniz --</option>
                                                        {accounts.map((acc) => (
                                                            <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                                                        ))}
                                                    </Input>
                                                    {formik.values.accountId && (
                                                        <Button color="outline-secondary" size="sm" onClick={handleResetAccount} className="rounded-pill px-3">
                                                            <i className="bx bx-refresh me-1"></i> Temizle
                                                        </Button>
                                                    )}
                                                </div>
                                                {formik.touched.accountId && (
                                                    <FormFeedback>{formik.errors.accountId}</FormFeedback>
                                                )}
                                            </div>
                                        )}

                                        {/* new_invoice ise fatura alanları */}
                                        {usingNewInvoice && (
                                            <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                                <h6 className="mb-3 fw-semibold">Fatura Bilgileri</h6>
                                                <FormGroup>
                                                    <Label>Cari/Bayi Tipi *</Label>
                                                    <Input
                                                        type="select"
                                                        name="invoiceAccountType"
                                                        onChange={handleAccountTypeChange}
                                                        value={formik.values.invoiceAccountType}
                                                        invalid={formik.touched.invoiceAccountType && !!formik.errors.invoiceAccountType}
                                                        className="rounded-3"
                                                    >
                                                        <option value="">-- Cari/Bayi tipi seçiniz --</option>
                                                        {ACCOUNT_TYPES.map((type) => (
                                                            <option key={type.value} value={type.value}>{type.label}</option>
                                                        ))}
                                                    </Input>
                                                    {formik.touched.invoiceAccountType && (
                                                        <FormFeedback>{formik.errors.invoiceAccountType}</FormFeedback>
                                                    )}
                                                </FormGroup>

                                                <FormGroup>
                                                    <Label>Şirket Adı *</Label>
                                                    <Input
                                                        name="invoiceCompanyName"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.invoiceCompanyName}
                                                        placeholder="Fatura şirket adı"
                                                        invalid={formik.touched.invoiceCompanyName && !!formik.errors.invoiceCompanyName}
                                                        className="rounded-3"
                                                    />
                                                    {formik.touched.invoiceCompanyName && (
                                                        <FormFeedback>{formik.errors.invoiceCompanyName}</FormFeedback>
                                                    )}
                                                </FormGroup>

                                                {formik.values.invoiceAccountType === "SOLE_PROPRIETORSHIP" ? (
                                                    <FormGroup>
                                                        <Label>T.C. Kimlik No *</Label>
                                                        <Input
                                                            name="invoiceTcNumber"
                                                            onChange={formik.handleChange}
                                                            value={formik.values.invoiceTcNumber}
                                                            placeholder="T.C. kimlik numarası (11 haneli)"
                                                            maxLength={11}
                                                            invalid={formik.touched.invoiceTcNumber && !!formik.errors.invoiceTcNumber}
                                                            className="rounded-3"
                                                        />
                                                        {formik.touched.invoiceTcNumber && (
                                                            <FormFeedback>{formik.errors.invoiceTcNumber}</FormFeedback>
                                                        )}
                                                    </FormGroup>
                                                ) : (
                                                    formik.values.invoiceAccountType &&
                                                    formik.values.invoiceAccountType !== "SOLE_PROPRIETORSHIP" && (
                                                        <FormGroup>
                                                            <Label>Vergi No *</Label>
                                                            <Input
                                                                name="invoiceTaxNumber"
                                                                onChange={formik.handleChange}
                                                                value={formik.values.invoiceTaxNumber}
                                                                placeholder="Vergi numarası (10 haneli)"
                                                                maxLength={10}
                                                                invalid={formik.touched.invoiceTaxNumber && !!formik.errors.invoiceTaxNumber}
                                                                className="rounded-3"
                                                            />
                                                            {formik.touched.invoiceTaxNumber && (
                                                                <FormFeedback>{formik.errors.invoiceTaxNumber}</FormFeedback>
                                                            )}
                                                        </FormGroup>
                                                    )
                                                )}

                                                <FormGroup>
                                                    <Label>Vergi Dairesi *</Label>
                                                    <Input
                                                        name="invoiceTaxOffice"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.invoiceTaxOffice}
                                                        placeholder="Vergi dairesi"
                                                        invalid={formik.touched.invoiceTaxOffice && !!formik.errors.invoiceTaxOffice}
                                                        className="rounded-3"
                                                    />
                                                    {formik.touched.invoiceTaxOffice && (
                                                        <FormFeedback>{formik.errors.invoiceTaxOffice}</FormFeedback>
                                                    )}
                                                </FormGroup>

                                                <FormGroup>
                                                    <Label>Adres *</Label>
                                                    <Input
                                                        type="textarea"
                                                        name="invoiceAddress"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.invoiceAddress}
                                                        placeholder="Fatura adresi"
                                                        rows={2}
                                                        invalid={formik.touched.invoiceAddress && !!formik.errors.invoiceAddress}
                                                        className="rounded-3"
                                                    />
                                                    {formik.touched.invoiceAddress && (
                                                        <FormFeedback>{formik.errors.invoiceAddress}</FormFeedback>
                                                    )}
                                                </FormGroup>

                                                <Row>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>E-posta</Label>
                                                            <Input
                                                                type="email"
                                                                name="invoiceEmail"
                                                                onChange={formik.handleChange}
                                                                value={formik.values.invoiceEmail}
                                                                placeholder="email@example.com"
                                                                invalid={formik.touched.invoiceEmail && !!formik.errors.invoiceEmail}
                                                                className="rounded-3"
                                                            />
                                                            {formik.touched.invoiceEmail && (
                                                                <FormFeedback>{formik.errors.invoiceEmail}</FormFeedback>
                                                            )}
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <PhoneInput
                                                            label="Telefon"
                                                            name="invoicePhone"
                                                            value={formik.values.invoicePhone}
                                                            onChange={formik.handleChange}
                                                        />
                                                        {formik.touched.invoicePhone && formik.errors.invoicePhone && (
                                                            <div className="invalid-feedback d-block">
                                                                {formik.errors.invoicePhone}
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}

                                        {/* Taksit grubu — başlık satırında "Yeni Grup Ekle" */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                                                <h6 className="mb-0 fw-semibold">Taksit Grubu</h6>
                                                {/* grup ekleme */}
                                                {(() => {
                                                    const forbidden = ["ROLE_COMPANY_CASHIER", "ROLE_ACCOUNT_ADMIN", "ROLE_ACCOUNT_ACCOUNTANT"];
                                                    const canAddGroup = !roles.some((r) => forbidden.includes(r));
                                                    return canAddGroup ? (
                                                        <Button color="success" size="sm" onClick={openAddGroupModal} className="btn-rounded px-3">
                                                            <i className="bx bx-plus me-1"></i> Yeni Grup Ekle
                                                        </Button>
                                                    ) : null;
                                                })()}
                                            </div>

                                            <FormGroup>
                                                <Input
                                                    type="select"
                                                    name="accountGroupId"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.accountGroupId}
                                                    disabled={filteredGroups.length === 0}
                                                    invalid={formik.touched.accountGroupId && !!formik.errors.accountGroupId}
                                                    className="rounded-3"
                                                >
                                                    <option value="">Seçiniz</option>
                                                    {filteredGroups.map((g) => (
                                                        <option key={g.id} value={g.id}>{g.groupName}</option>
                                                    ))}
                                                </Input>
                                                {filteredGroups.length === 0 && (
                                                    <small className="text-danger">
                                                        Seçilen cari/bayi için atalı bir taksit grubu yok.
                                                    </small>
                                                )}
                                                {formik.touched.accountGroupId && (
                                                    <FormFeedback>{formik.errors.accountGroupId}</FormFeedback>
                                                )}
                                            </FormGroup>

                                            <div className="d-flex justify-content-between justify-content-sm-end my-2">
                                                <Button
                                                    color="light" size="sm"
                                                    onClick={() => setShowRates((s) => !s)}
                                                    disabled={!formik.values.accountGroupId}
                                                    title="Seçili grubun taksit oranlarını görüntüle"
                                                    className="btn-rounded px-3"
                                                >
                                                    <i className={`bx ${showRates ? "bx-hide" : "bx-show"} me-1`} />
                                                    {showRates ? "Taksit Oranlarını Gizle" : "Taksit Oranlarını Göster"}
                                                </Button>
                                            </div>

                                            {!showRates && formik.values.accountGroupId && (
                                                <Alert color="info" className="py-2 border-0 rounded-4">
                                                    Taksit oranlarını görmek için <strong>“Taksit Oranlarını Göster”</strong> düğmesine tıklayın.
                                                </Alert>
                                            )}

                                            <Collapse isOpen={!!formik.values.accountGroupId && showRates}>
                                                {formik.values.accountGroupId && renderInstallmentCards()}
                                            </Collapse>
                                        </div>

                                        {/* Link tipi, geçerlilik ve tutar */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                            <h6 className="mb-3 fw-semibold">Link Tipi, Geçerlilik ve Tutar</h6>

                                            <FormGroup className="mb-3">
                                                <Label className="d-block">Link Tipi</Label>
                                                <div className="d-flex flex-wrap gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        color={formik.values.linkType === "ONE_TIME" ? "primary" : "outline-primary"}
                                                        className="btn-rounded px-3"
                                                        onClick={() => formik.setFieldValue("linkType", "ONE_TIME")}
                                                    >
                                                        Tek Kullanımlık
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        color={formik.values.linkType === "FIXED" ? "primary" : "outline-primary"}
                                                        className="btn-rounded px-3"
                                                        onClick={() => formik.setFieldValue("linkType", "FIXED")}
                                                    >
                                                        Sabit Link
                                                    </Button>
                                                </div>
                                                {formik.values.linkType === "FIXED" && (
                                                    <small className="text-muted d-block mt-1">
                                                        Sabit link kalıcıdır ve birden çok {pw.word} kabul eder; QR koda veya faturaya
                                                        basılabilir. {pw.Word} Linkleri sayfasından istediğiniz an duraklatabilirsiniz.
                                                    </small>
                                                )}
                                            </FormGroup>

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>
                                                            Bitiş Tarihi{formik.values.linkType === "FIXED" ? " (opsiyonel)" : ""}
                                                        </Label>
                                                        <DateTimeInput
                                                            value={formik.values.expiryDate}
                                                            onChange={(v) => formik.setFieldValue("expiryDate", v)}
                                                            invalid={formik.touched.expiryDate && !!formik.errors.expiryDate}
                                                            minDate={nowLocalDateTime()}
                                                            className="rounded-3"
                                                        />
                                                        {formik.values.linkType === "FIXED" && (
                                                            <small className="text-muted">Boş bırakılırsa link süresiz geçerli olur.</small>
                                                        )}
                                                        {formik.touched.expiryDate && (
                                                            <FormFeedback>{formik.errors.expiryDate}</FormFeedback>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup className="mb-0">
                                                        <Label>Sabit Tutar (opsiyonel)</Label>
                                                        <div className="input-group">
                                                            <Input
                                                                type="text"
                                                                inputMode="decimal"
                                                                name="fixedAmount"
                                                                value={displayFixedAmount}
                                                                onChange={handleFixedAmountChange}
                                                                invalid={formik.touched.fixedAmount && !!formik.errors.fixedAmount}
                                                                placeholder="Örn: 1.250,50"
                                                                className="rounded-start-3"
                                                            />
                                                            {/* Tahsilat girişi ekranıyla aynı desen: tutarın sağında para birimi seçimi */}
                                                            <Input
                                                                type="select"
                                                                name="currency"
                                                                value={formik.values.currency}
                                                                onChange={formik.handleChange}
                                                                disabled={!!prefilledDebtMovementId}
                                                                style={{ maxWidth: 120 }}
                                                                className="rounded-end-3"
                                                            >
                                                                {CURRENCIES.map((cur) => (
                                                                    <option key={cur.code} value={cur.code}>{cur.label}</option>
                                                                ))}
                                                            </Input>
                                                        </div>
                                                        {formik.touched.fixedAmount && formik.errors.fixedAmount && (
                                                            <div className="invalid-feedback" style={{ display: "block" }}>
                                                                {formik.errors.fixedAmount}
                                                            </div>
                                                        )}
                                                        <small className="text-muted">Doldurursanız ödeme sayfasında tutar değiştirilemez.</small>
                                                        {prefilledDebtMovementId && (
                                                            <small className="text-muted d-block">
                                                                Para birimi bağlı borç hareketinden gelir, değiştirilemez.
                                                            </small>
                                                        )}
                                                        {formik.values.currency !== "TRY" && (
                                                            <small className="text-info d-block mt-1">
                                                                <i className="bx bx-info-circle me-1" />
                                                                Tutar {formik.values.currency} cinsindendir. Ödeme sayfasında güncel kurla TL
                                                                karşılığı gösterilir ve tahsilat TL olarak yapılır.
                                                            </small>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            {formik.values.linkType === "FIXED" &&
                                                String(formik.values.fixedAmount ?? "").trim() === "" && (
                                                    <Row className="mt-3">
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>Minimum Tutar (₺)</Label>
                                                                <Input
                                                                    type="number"
                                                                    name="minAmount"
                                                                    min="10"
                                                                    step="0.01"
                                                                    value={formik.values.minAmount}
                                                                    onChange={formik.handleChange}
                                                                    invalid={formik.touched.minAmount && !!formik.errors.minAmount}
                                                                    placeholder="Boş bırakılırsa 10"
                                                                    className="rounded-3"
                                                                />
                                                                {formik.touched.minAmount && (
                                                                    <FormFeedback>{formik.errors.minAmount}</FormFeedback>
                                                                )}
                                                                <small className="text-muted">
                                                                    Kart deneme (card-testing) saldırılarına karşı en az 10 ₺.
                                                                </small>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label>Maksimum Tutar (₺, opsiyonel)</Label>
                                                                <Input
                                                                    type="number"
                                                                    name="maxAmount"
                                                                    step="0.01"
                                                                    value={formik.values.maxAmount}
                                                                    onChange={formik.handleChange}
                                                                    invalid={formik.touched.maxAmount && !!formik.errors.maxAmount}
                                                                    placeholder="Sınırsız"
                                                                    className="rounded-3"
                                                                />
                                                                {formik.touched.maxAmount && (
                                                                    <FormFeedback>{formik.errors.maxAmount}</FormFeedback>
                                                                )}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                )}

                                            {formik.values.linkType === "FIXED" && (
                                                <FormGroup className="mt-1">
                                                    <Label>Maksimum Kullanım Sayısı (opsiyonel)</Label>
                                                    <Input
                                                        type="number"
                                                        name="maxUsageCount"
                                                        min="1"
                                                        step="1"
                                                        value={formik.values.maxUsageCount}
                                                        onChange={formik.handleChange}
                                                        invalid={formik.touched.maxUsageCount && !!formik.errors.maxUsageCount}
                                                        placeholder="Sınırsız"
                                                        className="rounded-3"
                                                    />
                                                    {formik.touched.maxUsageCount && (
                                                        <FormFeedback>{formik.errors.maxUsageCount}</FormFeedback>
                                                    )}
                                                    <small className="text-muted">
                                                        Bu sayıda başarılı {pw.ablative} sonra link otomatik kapanır.
                                                    </small>
                                                </FormGroup>
                                            )}
                                        </div>

                                        {/* Link çıktısı */}
                                        {link && (
                                            <Alert color="success" className="border-0 rounded-4">
                                                <i className="bx bx-check-circle me-2"></i>
                                                <strong>Bağlantı Oluşturuldu:</strong> {link}
                                                <div className="mt-2 d-flex flex-column flex-sm-row justify-content-end gap-2">
                                                    <Button color="success" size="sm" href={link} target="_blank" className="rounded-pill px-3">
                                                        <i className="bx bx-link-external me-1" /> Aç
                                                    </Button>
                                                    <Button color="outline-success" size="sm" onClick={() => navigator.clipboard.writeText(link)} className="rounded-pill px-3">
                                                        <i className="bx bx-copy me-1" /> Kopyala
                                                    </Button>
                                                </div>
                                            </Alert>
                                        )}

                                        {/* Hata satırı */}
                                        {error && (
                                            <Alert color="danger" className="border-0 rounded-4">
                                                <i className="bx bx-error-circle me-2"></i>
                                                <strong>Hata:</strong> {error}
                                            </Alert>
                                        )}

                                        {/* Aksiyonlar */}
                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-light btn-rounded px-4"
                                                style={{ height: 34, fontSize: ".8rem" }}
                                                onClick={() => navigate(-1)}
                                            >
                                                İptal
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-navy btn-rounded px-4 d-flex align-items-center gap-1"
                                                style={{ height: 34, fontSize: ".8rem" }}
                                                onClick={handleSubmitVisibleOnly}
                                                disabled={
                                                    loadingGroups || !flowChosen || !formik.values.accountGroupId || formik.isSubmitting
                                                }
                                            >
                                                {formik.isSubmitting ? (
                                                    <><Spinner size="sm" className="me-1" /> Oluşturuluyor...</>
                                                ) : (
                                                    <><i className="bx bx-plus-circle" style={{ fontSize: 16 }} /> Bağlantı Oluştur</>
                                                )}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <ToastContainer position="top-right" autoClose={3000} />

            {/* Yeni Grup Ekleme Modal */}
            <Modal
                isOpen={addGroupModalOpen}
                toggle={() => setAddGroupModalOpen(false)}
                size={isMobile ? "" : "lg"}
                style={{ maxWidth: isMobile ? "95vw" : "90vw", width: isMobile ? "95vw" : "50vw", height: "90vh", margin: "5vh auto" }}
            >
                <ModalHeader toggle={() => setAddGroupModalOpen(false)}>Yeni Taksit Grubu Oluştur</ModalHeader>
                <ModalBody style={{ height: "calc(90vh - 120px)", overflowY: "auto", overflowX: "hidden", paddingRight: isMobile ? "8px" : "15px" }}>
                    <Form>
                        <FormGroup>
                            <Label>Grup Adı *</Label>
                            <Input
                                name="groupName"
                                value={groupData?.groupName || ""}
                                onChange={handleGroupChange}
                                style={{ borderRadius: "0" }}
                                required
                                disabled={loadingGroupSave}
                                placeholder="Grup adını giriniz"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Açıklama</Label>
                            <Input
                                type="textarea"
                                name="description"
                                value={groupData?.description || ""}
                                onChange={handleGroupChange}
                                style={{ borderRadius: "0" }}
                                rows={3}
                                disabled={loadingGroupSave}
                                placeholder="Grup açıklaması (opsiyonel)"
                            />
                        </FormGroup>
                        <hr />
                        <h5 className="mb-2" style={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>Taksit Oranları</h5>
                        <div className="mb-3">
                            <Button
                                color="info"
                                onClick={handleFetchPayTRRates}
                                disabled={loadingGroupSave || !companyId}
                                style={{ marginRight: "1rem", borderRadius: "0" }}
                            >
                                {loadingGroupSave ? (<><Spinner size="sm" className="me-2" /> Yükleniyor...</>) : ("PayTR Taksit Oranlarını Çek")}
                            </Button>
                        </div>

                        {CARD_TYPES.map((card) => (
                            <div key={card} className="mb-4">
                                <strong className="text-primary" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>{card}</strong>
                                <Row className="g-2 mt-1">
                                    {[...Array(MAX_INSTALLMENTS)].map((_, i) => {
                                        const inst = i + 1;
                                        const cellData = groupData.rates?.[card]?.[inst];
                                        const isEnabled = cellData ? cellData.enabled !== false : true;
                                        const cellValue = cellData ? cellData.value || "" : "";

                                        return (
                                            <Col key={inst} xs={12} sm={6} md={4} lg={3} xl={2}>
                                                <div className="border p-2 p-sm-3" style={{ borderRadius: isMobile ? "6px" : "8px", backgroundColor: "#f8f9fa" }}>
                                                    <div
                                                        className="d-flex align-items-center mb-2 mb-sm-3"
                                                        onClick={() => handleEnabledChange(card, inst, !cellData?.enabled)}
                                                        style={{ cursor: "pointer", userSelect: "none" }}
                                                    >
                                                        <input className="form-check-input me-2" type="checkbox" checked={isEnabled} readOnly style={{ transform: isMobile ? "scale(0.9)" : "none" }} />
                                                        <label className="form-check-label mb-0 fw-medium" style={{ cursor: "pointer", fontSize: isMobile ? "0.8rem" : "0.875rem", color: isEnabled ? "#495057" : "#6c757d" }}>
                                                            {inst} Taksit
                                                        </label>
                                                    </div>
                                                    <div>
                                                        <Label className="mb-2 text-muted fw-medium" style={{ fontSize: isMobile ? "0.75rem" : "0.8rem" }}>
                                                            Komisyon (%)
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            value={cellValue}
                                                            onChange={(e) => handleRateChange(card, inst, e.target.value)}
                                                            disabled={loadingGroupSave || !isEnabled}
                                                            style={{
                                                                borderRadius: "6px",
                                                                border: isEnabled ? "1px solid #ced4da" : "1px solid #e9ecef",
                                                                backgroundColor: isEnabled ? "#fff" : "#f8f9fa",
                                                                padding: isMobile ? "0.25rem 0.5rem" : undefined,
                                                                fontSize: isMobile ? "0.875rem" : undefined,
                                                                height: isMobile ? "2rem" : undefined
                                                            }}
                                                            size="sm"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        ))}
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setAddGroupModalOpen(false)} style={{ borderRadius: "0" }} disabled={loadingGroupSave}>
                        İptal
                    </Button>
                    <Button color="success" onClick={handleSaveGroup} style={{ borderRadius: "0" }} disabled={!groupData.groupName?.trim() || loadingGroupSave}>
                        {loadingGroupSave ? (<><Spinner size="sm" className="me-2" /> Kaydediliyor...</>) : ("Kaydet")}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}